import apiClient from './client';

export interface PushNotificationPayload {
  title: string;
  body: string;
  data?: Record<string, any>;
  badge?: number;
  sound?: string;
}

export interface SendToAllPayload extends PushNotificationPayload {}

export interface SendToUsersPayload extends PushNotificationPayload {
  userIds: number[];
}

export interface SendToRolesPayload extends PushNotificationPayload {
  roles: string[];
}

export interface NotificationHistory {
  id: number;
  title: string;
  body: string;
  data: Record<string, any>;
  targetType: 'all' | 'specific' | 'role';
  totalRecipients: number;
  successfulSends: number;
  failedSends: number;
  sentAt: string;
  sentBy?: {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
  };
}

export interface PushNotificationStats {
  devices: {
    total: number;
    active: number;
    inactive: number;
  };
  notifications: {
    last30Days: number;
    successfulSends: number;
    failedSends: number;
  };
}

/**
 * Send push notification to all users
 */
export const sendToAll = async (payload: SendToAllPayload) => {
  const { data } = await apiClient.post('/push-notifications/send-to-all', payload);
  return data;
};

/**
 * Send push notification to specific users
 */
export const sendToUsers = async (payload: SendToUsersPayload) => {
  const { data } = await apiClient.post('/push-notifications/send-to-users', payload);
  return data;
};

/**
 * Send push notification to users with specific roles
 */
export const sendToRoles = async (payload: SendToRolesPayload) => {
  const { data } = await apiClient.post('/push-notifications/send-to-roles', payload);
  return data;
};

/**
 * Get notification history
 */
export const getNotificationHistory = async (limit = 50, offset = 0): Promise<{
  data: NotificationHistory[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
  };
}> => {
  const { data } = await apiClient.get('/push-notifications/history', {
    params: { limit, offset }
  });
  return data;
};

/**
 * Get push notification statistics
 */
export const getStats = async (): Promise<{ data: PushNotificationStats }> => {
  const { data } = await apiClient.get('/push-notifications/stats');
  return data;
};

/**
 * Delete a push notification
 */
export const deleteNotification = async (id: number): Promise<{ success: boolean; message: string }> => {
  const { data } = await apiClient.delete(`/push-notifications/${id}`);
  return data;
};

export interface VerifyCheckItem {
  name: string;
  set: boolean;
  masked: string | null;
}

export interface VerifyCheck {
  id: string;
  name: string;
  status: 'pass' | 'fail' | 'warn' | 'info';
  details: string;
  items?: VerifyCheckItem[];
  breakdown?: Record<string, any> | null;
}

export interface VerifyResult {
  overall: {
    status: 'healthy' | 'warning' | 'error';
    message: string;
    counts: { pass: number; fail: number; warn: number };
  };
  checks: VerifyCheck[];
  verifiedAt: string;
}

/**
 * Run comprehensive push notification setup verification
 */
export const verifySetup = async (): Promise<{ data: VerifyResult }> => {
  const { data } = await apiClient.get('/push-notifications/verify');
  return data;
};

/**
 * Send a test push notification to verify APNs connectivity
 */
export const sendTestNotification = async (deviceToken?: string): Promise<{ success: boolean; message: string; data: any }> => {
  const { data } = await apiClient.post('/push-notifications/test', { deviceToken });
  return data;
};

export default {
  sendToAll,
  sendToUsers,
  sendToRoles,
  getNotificationHistory,
  getStats,
  deleteNotification,
  verifySetup,
  sendTestNotification,
};

